/**
 * Typed client for /admin-charity/* — backs the Charity Verify screen.
 *
 * Donation-mode apps with a populated `causeProfile.taxIdNumber` (EIN)
 * may claim 501(c)(3) status. NUREV staff verifies each claim manually
 * via the IRS Tax-Exempt Organization Search lookup tool, then flips
 * `causeProfile.isVerifiedCharity` true/false via these endpoints. Both
 * actions write an audit-log entry server-side.
 *
 * Endpoints:
 *   GET    /admin-charity/queue            — apps awaiting verification
 *   GET    /admin-charity/verified         — apps already verified
 *   POST   /admin-charity/:appId/verify    — flip flag true (body { notes })
 *   POST   /admin-charity/:appId/unverify  — flip flag false (body { reason })
 *
 * ENVELOPE: these endpoints return the standard `{ success, data }`
 * envelope (controller uses `utils.handleSuccess`), so every call goes
 * through `unwrap()`. (The legacy dashboard's axiosClient auto-stripped
 * one `.data` level, which is why the old hook read `data.docs`
 * directly — the admin `client` here does not, hence `unwrap`.)
 */
import { client, unwrap } from './client';

export interface CauseProfile {
  mission: string;
  photoUrl: string;
  websiteUrl: string;
  taxIdNumber: string;
  isVerifiedCharity: boolean;
}

export interface CharityRow {
  appId: string;
  appKey: string;
  appName: string;
  publisherName: string | null;
  publisherEmail: string | null;
  publisherId: string | null;
  causeProfile: CauseProfile;
  installs: number;
  liveTime: number;
  createdAt: string;
  updatedAt: string;
}

export interface CharityListResponse {
  docs: CharityRow[];
  totalDocs: number;
}

export interface CharityVerifyResponse {
  appId: string;
  isVerifiedCharity: boolean;
}

export interface VerifyCharityInput {
  appId: string;
  notes: string;
}

export interface UnverifyCharityInput {
  appId: string;
  reason: string;
}

// ─── Fetchers ─────────────────────────────────────────────────────────────

// Pending-verification queue: donation-mode apps with EIN claimed but
// not yet flipped to isVerifiedCharity=true.
export async function fetchCharityQueue(): Promise<CharityListResponse> {
  return unwrap<CharityListResponse>(
    client.get('/admin-charity/queue')
  );
}

// Already-verified list (for review / unverify if needed).
export async function fetchCharityVerified(): Promise<CharityListResponse> {
  return unwrap<CharityListResponse>(
    client.get('/admin-charity/verified')
  );
}

export async function verifyCharity(
  input: VerifyCharityInput
): Promise<CharityVerifyResponse> {
  const { appId, notes } = input;
  return unwrap<CharityVerifyResponse>(
    client.post(`/admin-charity/${appId}/verify`, { notes })
  );
}

export async function unverifyCharity(
  input: UnverifyCharityInput
): Promise<CharityVerifyResponse> {
  const { appId, reason } = input;
  return unwrap<CharityVerifyResponse>(
    client.post(`/admin-charity/${appId}/unverify`, { reason })
  );
}
